import { Service } from "../../../glue/Kernel.js";
import { WEAPONS, DEFAULT_LOADOUT } from "../../../content/index.js";
import { ITEMS } from "../../../content/pickups.js";
import { SPAWN, VOID_Y } from "../../../engine/chunks.js";
import { computeView, drawScene, screenToWorld } from "./renderer.js";
import { ownedBlocks } from "./wheel.js";
import { WidgetLayer } from "../ui/widget-dom.js";
import { SOLID_MATERIALS } from "../../../engine/tiles.js";
import { reachTile, DEFAULT_REACH } from "../../../engine/reach.js";
import { interpolate, advanceGait, newGait, advanceZoom, ZOOM } from "./interp.js";
import { createSpriteVM } from "./sprite-vm.js";
import { setSpriteVM, setSpriteTime, setItemDefs, setIconCanvas, setBlockDefs } from "./sprites.js";

const MAT_FILL = Object.fromEntries(SOLID_MATERIALS.map((m) => [m.name, m.fill])); // material -> swatch colour
// [near, far] world-unit range a sound attenuates over: full within `near`, silent past
// `far`. Most sounds use DEFAULT (audible across the map); fire is deliberately short.
const DEFAULT_RANGE = [560, 2600];
const SFX_RANGE = { fire: [90, 500] };

// The screen: the canvas, the camera that follows the player through the infinite world,
// the interpolation between 30Hz snapshots, the walk-cycle gait, and the particle/deny
// fx. It reads the world from `net`'s Client, plays sound through `audio`, draws the chat
// feed and HUD flags from `ui`, and reads the aim/scoreboard toggle from `input`. It owns
// `view` and the deny ring, which InputService reaches for aim and click feedback.
export class RenderService extends Service {
    on_phase_init() {
        this.canvas = document.getElementById("stage");
        this.g = this.canvas.getContext("2d");
        this.particles = [];
        this.denies = [];                             // client-only "fired too early" flashes
        this.gaits = new Map();                       // entity id -> {phase, x, speed, walk}
        this.camera = { x: SPAWN.x, y: SPAWN.y };
        this.view = computeView(this.canvas, this.camera);
        const resize = () => { this.canvas.width = innerWidth; this.canvas.height = innerHeight; };
        addEventListener("resize", resize);
        resize();
    }

    on_phase_consolidate() {
        this.client = this.serviceAPI.require("net").client;
        this.audio = this.serviceAPI.require("audio");
        this.ui = this.serviceAPI.require("ui");
        this.input = this.serviceAPI.require("input");
        // Item icons rasterize once and blit thereafter (see sprites.js) — hand them a way to make an
        // offscreen canvas. Without this they fall back to redrawing the vector art every frame.
        setIconCanvas((w, h) => { const c = document.createElement("canvas"); c.width = w; c.height = h; return c; });
        // The fx stream is rendered here (particles, sfx); every other net.* event is UIService's.
        this.serviceAPI.require("net").on("net.state", (_name, _m, m) => { for (const e of m.fx || []) this.spawnFx(e); });
    }

    on_phase_ready() {
        this._last = performance.now();
        const loop = (now) => { this.frame(now); requestAnimationFrame(loop); };
        requestAnimationFrame(loop);
    }

    // A "fired too early" ring at the crosshair (InputService flashes it on a click that
    // lands while the weapon is still cooling down).
    pushDeny(x, y) { this.denies.push({ ttl: 0.4, max: 0.4, x, y }); }

    // Screen pixel -> world point, through the current camera view. InputService aims with
    // this, so `view` (and the projection maths in renderer.js) stay render-private.
    worldAt(sx, sy) { return screenToWorld(this.view, sx, sy); }

    // The tile the held build tool would act on, as a ghost for the renderer — null unless
    // the trowel or mallet is in hand and no overlay owns the pointer. `ok` says whether the
    // action would actually happen (an empty tile for the trowel, a solid one for the mallet).
    _buildGhost(selfEnt) {
        const client = this.client;
        if (!selfEnt || this.ui.wheelIsOpen() || this.ui.settingsOpen() || this.ui.inventoryOpen() || this.ui.menuIsOpen()) return null;
        const aim = this.worldAt(this.input.mouse.x, this.input.mouse.y);
        // The trowel / mallet (a weapon) previews the tile it would build / mine.
        const w = WEAPONS[DEFAULT_LOADOUT[client.input.weapon | 0]];
        if (w && (w.behavior === "build" || w.behavior === "mine")) {
            const { tcx, tri } = reachTile(selfEnt.x, selfEnt.y, aim.x, aim.y, w.reach);
            const solid = client.terrain.isSolid(tcx, tri);
            const place = w.behavior === "build";
            return { tcx, tri, place, ok: place ? !solid : solid };
        }
        // A PLACEABLE KIT in hand (campfire / block maker / drying rack — item manifest `place`) previews
        // the same dashed tile at the cursor + reach, so it drops where you aim, not where you stand.
        const held = client.heldItem, man = client.itemManifest || {};
        if (held && man[held] && man[held].place) {
            const { tcx, tri } = reachTile(selfEnt.x, selfEnt.y, aim.x, aim.y, DEFAULT_REACH);
            return { tcx, tri, place: true, ok: !client.terrain.isSolid(tcx, tri) };
        }
        return null;
    }

    // How loud a world-space sound is from where the camera (the local player) sits:
    // full up close, fading to silence far off, so a firefight across the map is a
    // distant patter and your own shots are front-and-centre.
    sfxAtten(x, y, name) {
        if (x == null || y == null) return 1;
        // Most sounds carry across the map (a firefight is a distant patter). A few are
        // short-range by nature — a fire you should only hear from a dozen-odd blocks off,
        // not across the level (TILE is 40, so ~12 blocks ≈ 500).
        const [NEAR, FAR] = SFX_RANGE[name] || DEFAULT_RANGE;
        const d = Math.hypot(x - this.camera.x, y - this.camera.y);
        if (d <= NEAR) return 1;
        if (d >= FAR) return 0;
        return 1 - (d - NEAR) / (FAR - NEAR);
    }

    spawnFx(e) {
        const client = this.client;
        const feed = this.ui.feed;
        if (e.t === "sfx") return this.audio.playSfx(e.sfx, this.sfxAtten(e.x, e.y, e.sfx)); // weapon fire, explosions, jettison, etc.
        if (e.t === "blockdef") { client.terrain.applyBlockDefs([[e.id, e.pixels]]); return; } // Set 19: a new custom-block design
        if (e.t === "terrain") { // a tile changed or a block got chipped; keep our mirror in step
            if (e.edits) client.terrain.applyEdits(e.edits);
            if (e.dmg) client.terrain.applyDamage(e.dmg);
            if (e.paints) client.terrain.applyPaint(e.paints); // Set 19: paint splatter delta
            if (e.customs) client.terrain.applyCustom(e.customs); // Set 19: placed custom blocks
        }
        else if (e.t === "splash") { // Set 19.1: a body crossing the water surface — a ring + droplets arcing up
            this.particles.push({ x: e.x, y: e.y, ttl: 0.4, max: 0.4, r0: 3, grow: 26, color: "#8fd0ff" });
            const n = 9;
            for (let i = 0; i < n; i++) {
                const a = -Math.PI / 2 + (i / (n - 1) - 0.5) * 2.1; // fan upward
                const sp = 120 + Math.random() * 150;
                this.particles.push({ x: e.x, y: e.y, ttl: 0.45 + Math.random() * 0.2, max: 0.65, r0: 2.6, grow: 0, fill: true,
                    color: i % 2 ? "#bfe6ff" : "#4a8fd0", vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, g: 950 });
            }
        }
        else if (e.t === "hit") this.particles.push({ x: e.x, y: e.y, ttl: 0.25, max: 0.25, r0: 3, grow: 10, color: "#ffd24a" });
        else if (e.t === "kill") this.particles.push({ x: e.x, y: e.y, ttl: 0.45, max: 0.45, r0: 6, grow: 34, color: "#ff5b6e" });
        else if (e.t === "died") this.particles.push({ x: e.x, y: e.y, ttl: 0.6, max: 0.6, r0: 10, grow: 60, color: "#39d1ff" });
        else if (e.t === "boom") { // jetpack explosion — a big, brief fireball
            this.particles.push({ x: e.x, y: e.y, ttl: 0.55, max: 0.55, r0: 14, grow: (e.r || 150) * 2, color: "#ff8a1e" });
            this.particles.push({ x: e.x, y: e.y, ttl: 0.4, max: 0.4, r0: 8, grow: (e.r || 150), color: "#ffe24a" });
        } else if (e.t === "thrust") {
            this.particles.push({ x: e.x, y: e.y, ttl: 0.22, max: 0.22, r0: 4, grow: 14, color: "#ff9a3a" });
            this.audio.jetThrust(this.sfxAtten(e.x, e.y)); // synth roar + blips while the jetpack burns
        }
        else if (e.t === "openmaker") {
            // The server says we pressed a machine's key beside a placed one (Set 19.2; generalised 20.2):
            // open that machine's client UI. The input name rides `e.ui` — the block maker's editor by
            // default, the campfire's cooking bench when it passes its own — and the owning plugin's
            // client.lua handles the on_input via the sprite VM.
            const ss = client.selfScore();
            if (ss && e.id === ss.id && this._vm && this._vm.fireInput) this._vm.fireInput(e.ui || "blockmaker.open", null);
        }
        else if (e.t === "weapon") {
            // The server moved our slot — we ran dry, or we just (re)spawned onto the one
            // weapon that's loaded. Follow it: our own input is what drives the selection,
            // so ignoring this would argue us straight back onto the empty gun.
            const ss = client.selfScore();
            if (ss && e.id === ss.id) {
                client.input.weapon = e.idx;
                if (e.why === "dry") feed(`out of ammo — switched to ${(WEAPONS[e.to] || {}).label || e.to}`, "system");
            }
        } else if (e.t === "pickup") {
            this.particles.push({ x: e.x, y: e.y, ttl: 0.4, max: 0.4, r0: 6, grow: 22, color: "#4dffa6" });
            // Only shout about your own — a busy room's worth of other people's pickups is
            // noise, and the thing you need to know is what YOU just got.
            const ss = client.selfScore();
            if (ss && e.by === ss.id) {
                const def = ITEMS[e.item];
                const w = def && def.weapon && WEAPONS[def.weapon];
                const msg = w ? `picked up ${w.label}  (+${def.ammo})`
                    : def && def.give === "jetpack" ? "picked up a JETPACK — press Z to arm"
                    : `picked up ${e.item}  (+${def ? def.amount : ""})`;
                feed(msg, "team");
            }
        } else if (e.t === "frag") {
            // The kill feed. The server sends the frag rather than the client inferring it
            // from scores changing, so the wording is the same for everyone watching.
            feed(e.killer ? `${e.killer} fragged ${e.victim}` : `${e.victim} died`, "frag");
        } else if (e.t === "matchend") {
            feed(e.winner ? `${e.winner} wins the match (${e.reason})` : `match drawn (${e.reason})`, "system");
        } else if (e.t === "matchstart") {
            feed("match start — go", "system");
        } else if (e.t === "capture") {
            feed(`${e.team.toUpperCase()} captured the flag!${e.by ? ` (${e.by})` : ""}`, "frag");
        } else if (e.t === "flagtake") {
            feed(`the ${e.team} flag was taken${e.by ? ` by ${e.by}` : ""}`, "team");
        } else if (e.t === "flagdrop") {
            feed(`the ${e.team} flag dropped`, "system");
        } else if (e.t === "flagreturn") {
            feed(`the ${e.team} flag is home`, "system");
        } else if (e.t === "koth") {
            feed(e.holder ? `${e.holder} holds the hill` : "the hill is up for grabs", "team");
        }
    }

    // The camera's smoothing (Set 15, plugin-driven since Set 16). A zoom PLUGIN calls set_zoom(f) to
    // say where it wants the camera; the engine only walks there. The curve lives in interp.js with the
    // rest of the presentation maths, so it's unit-testable without a canvas — tune ZOOM there.
    _zoom(dt) {
        const r = advanceZoom(this._zoomT || 0, dt, this._zoomTarget == null ? 1 : this._zoomTarget);
        this._zoomT = r.t;
        return r.zoom;
    }

    frame(now) {
        const client = this.client;
        const dt = Math.min(0.05, (now - this._last) / 1000);
        this._last = now;

        const alpha = Math.min(1, (now - client.curTime) / (1000 / 30));
        const ents = interpolate(client.prev, client.cur, alpha);

        // Advance each stick's walk-cycle gait (interp.js) and forget figures that have
        // left the frame, so their gait state doesn't leak.
        const live = new Set();
        for (const e of ents) {
            if (e.sh !== "stick") continue;
            live.add(e.id);
            let gt = this.gaits.get(e.id);
            if (!gt) { gt = newGait(e.x); this.gaits.set(e.id, gt); }
            advanceGait(gt, e, dt);
        }
        for (const id of this.gaits.keys()) if (!live.has(id)) this.gaits.delete(id);

        // Camera follows the self player through the infinite world.
        const ss = client.selfScore();
        // Charge weapons: mirror the server's wind-up locally (hold the trigger on a charge
        // weapon, once the cooldown's up) so the crosshair can show a fill ring. Visual only.
        const selW = WEAPONS[DEFAULT_LOADOUT[client.input.weapon | 0]];
        if (selW && selW.charge && client.input.fire && (ss ? ss.cd : 0) <= 0.001) {
            this.charge = Math.min(selW.charge.time, (this.charge || 0) + dt);
        } else {
            this.charge = 0;
        }
        this.chargeFrac = selW && selW.charge ? (this.charge || 0) / selW.charge.time : 0;
        const selfEnt = ss ? ents.find((e) => e.id === ss.id) : null;
        if (selfEnt) {
            const k = Math.min(1, dt * 9);
            this.camera.x += (selfEnt.x - this.camera.x) * k;
            this.camera.y += (selfEnt.y - this.camera.y) * k;
        }
        this.view = computeView(this.canvas, this.camera, this._zoom(dt));

        // Fire ambience: a looping crackle tied to the fire ZONES themselves, at the volume of
        // the nearest one — so it fades with distance and, crucially, stops once the last fire
        // burns out (its zone leaves the snapshot) instead of a long one-shot outliving it.
        let nearestFire = Infinity;
        for (const e of ents) if (e.sh === "zone") nearestFire = Math.min(nearestFire, Math.hypot(e.x - this.camera.x, e.y - this.camera.y));
        if (nearestFire < Infinity) {
            const [NEAR, FAR] = SFX_RANGE.fire;
            const atten = nearestFire <= NEAR ? 1 : nearestFire >= FAR ? 0 : 1 - (nearestFire - NEAR) / (FAR - NEAR);
            if (atten > 0) this.audio.playSfx("fire", atten);
        }

        for (let i = this.particles.length - 1; i >= 0; i--) {
            const pt = this.particles[i];
            pt.ttl -= dt;
            if (pt.vx != null) { pt.x += pt.vx * dt; pt.y += pt.vy * dt; pt.vy += (pt.g || 0) * dt; } // droplets arc + fall (splash)
            if (pt.ttl <= 0) this.particles.splice(i, 1);
        }
        for (let i = this.denies.length - 1; i >= 0; i--) {
            this.denies[i].ttl -= dt;
            if (this.denies[i].ttl <= 0) this.denies.splice(i, 1);
        }
        this.ui.ageChat(dt);

        const cur = client.cur;
        // The shooter weapon strip is a PLUGIN now (Set 12.9): the shooter-hotbar plugin's ui.carousel
        // reads it from self() (_selfState below). The engine no longer assembles a weapon carousel here.

        const hud = {
            wave: cur ? cur.wave : 1,
            phase: cur ? cur.phase : "intermission",
            scores: cur ? cur.scores : [],
            mode: cur ? cur.mode : null,
            match: cur ? cur.match : null,
            teams: cur ? cur.teams : null,
            light: cur && cur.env ? cur.env.light : 1, // world light level (day/night plugin); 1 = full day
            // KotH's scoreboard status line, fed by the koth plugin through env (like the wave counter).
            hillKing: cur && cur.env ? cur.env.hillKing || "" : "",
            hillContested: cur && cur.env ? !!cur.env.hillContested : false,

            room: client.room,
            selfCid: client.selfCid,
            self: ss,
            widgets: client.hudWidgets, // reactive-HUD widgets declared by plugins (from welcome)
            falling: !!(selfEnt && client.terrain.bounded && selfEnt.y > VOID_Y),
            terrain: client.terrain,

            // Block stock + the trowel's block wheel, for the HUD and the wheel overlay.
            blocks: ss ? ss.blocks : null,
            // Unified inventory (Set 12.5): the item -> count map + the item manifest (icon/label per
            // name), for the hotbar. The hotbar shows everything you're holding.
            inv: ss ? ss.inv : null,
            items: client.itemManifest,
            hotbar: client.hotbar, // default hotbar on? (a plugin, e.g. grid inventory, can suppress it)
            wheel: { open: this.ui.wheelIsOpen(), sel: this.ui.wheelSel, items: ownedBlocks(ss ? ss.inv : null, (id) => this.client.terrain && this.client.terrain.blockDef(id)) },
            // A plugin's open template menu (UI framework D): its options + the hovered index.
            menu: this.ui.menuIsOpen() ? { title: this.ui.menuOpen.spec.title, options: this.ui.menuOpen.spec.options || [], sel: this.ui.menuSel } : null,
            // The open radial picker (Set 21.5): the plugin's options + the hovered index. Each option is
            // enriched here with its icon/tint/label from the item manifest (so a plugin can pass bare
            // { value } for its own items); an option that brings its own color/label (a block material)
            // keeps them. The renderer's one drawWheel draws whatever each option carries.
            picker: this.ui.pickerIsOpen() ? {
                sel: this.ui.picker.sel,
                options: this.ui.picker.options.map((o) => {
                    const m = (this.client.itemManifest || {})[o.value] || {};
                    return { value: o.value, count: o.count, color: o.color,
                        icon: o.icon || m.icon || "", tint: o.tint || m.tint || "", label: o.label || m.label || o.value };
                }),
            } : null,
            // A dashed ghost at the tile the trowel would fill / the mallet would break, so
            // the grid alignment is obvious. Same reach math the server edits with.
            buildGhost: this._buildGhost(selfEnt),
            mouse: this.input.mouse,
            chargeFrac: this.chargeFrac || 0, // dodgeball/fish wind-up, for the crosshair ring
            cd: ss ? ss.cd : 0,
            cdMax: ss ? ss.cdMax : 0,
            denies: this.denies,
            chat: this.ui.chatFeed,
            showScores: this.input.showScores,
            typing: this.ui.con.isTyping(),
        };
        this.ui.updateInventory(hud.inv); // keep the block panel live while it's open

        // Plugin sprite VM (Set 12.5): (re)build it when the room's client.lua set changes (join /
        // room switch), and stamp the wall clock so animated sprites move. Base sprites stay pure JS.
        const scripts = this.client.clientScripts || [];
        const imgs = this.client.clientImages || [];
        const sig = scripts.map((s) => s.name).join(",") + "|" + imgs.map((i) => i.name).join(",");
        if (sig !== this._vmSig) {
            this._vmSig = sig;
            // A live map the VM keeps a reference to: each asset is inserted only once it has decoded, so
            // image() never draws a half-loaded picture — it simply pops in on the frame after it's ready.
            this._images = {};
            for (const im of imgs) { const el = new Image(); el.onload = () => { this._images[im.name] = el; }; el.src = im.dataUri; }
            const vm = scripts.length ? createSpriteVM(scripts, this._images) : null;
            // Wire the client-plugin runtime's send() to the network (Set 12.5): a plugin's client half
            // messages its own server half plugin-private.
            if (vm) {
                vm.setSend((plugin, event, data) => this.client.sendPluginMessage(plugin, event, data));
                // ui.wheel({options, on_pick}) -> open the ONE radial picker with the plugin's options; the
                // pick resolves the plugin's on_pick (Set 21.5). Options are enriched with manifest icons in
                // the snapshot (picker field below), so the plugin can pass bare { value } for its own items.
                vm.setPicker((options, resolve) => this.ui.openPicker(options, resolve));
                // self() -> the local player's synced state, so a macro-widget grid's cells provider can
                // read self().inventory.slots (Set 12.5).
                vm.setSelf(() => this._selfState());
                // hold() -> record the held item (Set 12.11). A hotbar plugin's controls call it to select;
                // the engine derives the fire weapon from it. The plugin owns all the selection LOGIC.
                vm.setHold((name) => this._hold(name));
                // set_zoom(f) — a zoom plugin's one lever (Set 16). It names a target; the engine eases.
                if (vm.setZoom) vm.setZoom((f) => { this._zoomTarget = Math.max(1, Math.min(ZOOM.max, f || 1)); });
            }
            this._vm = vm;
            setSpriteVM(vm);
        }
        // How an item's icon + tint are looked up when something HELD has no art of its own (Set 14):
        // straight off the server's item manifest, the same table the inventory faces read.
        setItemDefs((name) => this.client.itemManifest[name] || null);
        setBlockDefs((id) => this.client.terrain && this.client.terrain.blockDef(id)); // custom-block designs for their icons (Set 19.2)
        setSpriteTime(performance.now() / 1000);

        drawScene(this.g, this.canvas, this.view, ents, this.particles, hud);

        // The plugin's macro-widgets (Set 12.5) are a DOM overlay on top of the canvas: reconcile it to
        // the current widget tree each frame (cheap — it only rebuilds when the widgets or cells change).
        if (!this.widgetLayer) this.widgetLayer = new WidgetLayer();
        this.widgetLayer.sync(this._vm, this.client.itemManifest);
    }

    // The local player's synced state, handed to client.lua via self() (Set 12.5 Option B). The server
    // owns the arrangement: score.order is the per-slot item string; split it into fixed slots (empties
    // are real "false" cells) and read each count from score.inv. The hotbar is the first row (8).
    _selfState() {
        const ss = this.client.selfScore();
        if (!ss) return null;
        const inv = ss.inv || {};
        // `infinite` rides along so the ONE item representation (items.ui.item) can show ∞ rather than
        // a meaningless "1" for a tool you can't run out of — the trowel holds a single marker count.
        // `unique` (Set 17) is that same problem for a GUN: you own one shoop, and "1" is not news —
        // its rounds are a separate item carrying the number worth showing. Read off the manifest,
        // because which items are unique is the server's registry to say.
        // `label` is the item's display name, for whatever the cell wants to say on hover (Set 17.1) —
        // the manifest's, falling back to the raw name so a cell is never nameless.
        const man = this.client.itemManifest || {};
        // An item may carry a KV data suffix ("cookedpasta?cups=6"); its manifest face (label/uses/unique)
        // comes from the BASE item before the "?", so a data variant needs no manifest entry of its own.
        const slots = (ss.order != null ? String(ss.order).split(",") : [])
            .map((item) => {
                // An INSTANCED item's slot is a "<name>@<eid>" token (Set 21.1): it's a single carried entity,
                // not a count-map entry, so it always counts as one and shows no count badge (unique-style).
                const inst = !!(item && item.includes("@"));
                const count = inst ? 1 : inv[item];
                if (!(item && count > 0)) return false;
                const base = item.split(/[?@]/)[0], mm = man[item] || man[base] || {};
                return { item, count, infinite: !!WEAPONS[item]?.ammo?.infinite,
                    unique: inst || !!mm.unique, label: mm.label || WEAPONS[item]?.label || base,
                    // `uses` (Set 19): the item's full uses count, so the cell draws a durability bar
                    // (count/uses) for a wear-out item. 0/absent for everything else.
                    uses: (WEAPONS[item]?.uses || mm.uses || 0) };
            });
        // The hotbar is the BOTTOM row of the 8x5 grid (last 8 slots) — Eric; the modal's hotbar line +
        // the HUD strip both mirror it, sitting right under the pack's bottom row.
        //
        // The shooter carousel's weapon strip (Set 12.9), for the carousel PLUGIN's ui.carousel: the
        // loadout weapons you can use — armed ones, the always-there tools, and whatever's selected — each
        // with its ammo (infinite = ∞), the trowel's block count + swatch, and its label. `selected` indexes
        // this list. Mirrors the engine carousel's own rule (kept as the fallback) so the two look the same.
        const selIndex = this.client.input.weapon | 0;
        const weapons = [];
        let selected = 0;
        for (let i = 0; i < DEFAULT_LOADOUT.length; i++) {
            const n = DEFAULT_LOADOUT[i], w = WEAPONS[n];
            if (!w) continue;                                    // a loadout name the weapon table doesn't know — skip, don't crash all of self()
            let count = 0, infinite = false, swatch = null;
            if (w.behavior === "build" && ss.blocks) {           // the trowel: its selected block's stock + swatch
                const sel = ss.blocks.sel;
                count = (ss.inv && ss.inv[sel]) || 0;            // materials are ordinary items (Set 14)
                swatch = MAT_FILL[sel] || null;
            } else if (w.ammo && w.ammo.infinite) { infinite = true; }
            else if (w.uses) { count = (ss.inv && ss.inv[n]) || 0; } // a usage weapon (Set 19): charges are the gun item's own count
            else { count = ss.ammo ? (ss.ammo[n] || 0) : 0; }
            const armed = infinite || count > 0;
            if (!(armed || w.tool || i === selIndex)) continue;  // dry guns drop out of the strip
            if (i === selIndex) selected = weapons.length;
            weapons.push({ item: n, count, infinite, swatch, label: WEAPONS[n].label, uses: w.uses || 0 });
        }
        const cdMax = ss.cdMax || 0, ready = cdMax > 0 ? 1 - (ss.cd || 0) / cdMax : 1;
        return {
            hp: ss.hp, ar: ss.ar, inventory: { slots, hotbar: slots.slice(-8) },
            weapons, weaponIndex: selected, ready,
            // Per-player HUD stats a plugin wrote with engine.setStat (hunger's food, the stove's pot
            // state, …). A plugin's client.lua reads self().hud to drive its own widgets — the stove
            // bench reads self().hud.potHas / potOutName. Without this the bench can never see the pot.
            hud: ss.hud || null,
            // The item currently held — ANY item name (Set 12.11), so a hotbar UI can highlight its cell and
            // cycle its own list by it. Set by hold(); before you've touched a control it falls back to the
            // armed weapon, so you start out holding your gun.
            held: this.client.heldItem != null ? this.client.heldItem : (weapons[selected] ? weapons[selected].item : null),
        };
    }

    // Hold an item by NAME — the generic primitive a hotbar plugin calls (Set 12.11). Selection is JUST
    // selection: it records the held item (drives the highlight, and the strip the plugin cycles). Firing
    // is a SEPARATE concern that reads the held item — so if the held item is a weapon we also point the
    // fire channel (input.weapon) at it; a non-weapon (a seed) is simply held, leaving your armed gun
    // alone. The plugin owns the LOGIC (which item is next, what a number key means); this owns nothing
    // but "this is now held, and here's the weapon firing should use."
    _hold(name) {
        this.client.heldItem = name;
        // Point the fire channel at the held weapon — or -1 when you hold a non-weapon (a seed), so the
        // server knows no weapon is in hand: it won't fire and the figure draws empty-handed (Set 12.12).
        this.client.input.weapon = DEFAULT_LOADOUT.indexOf(name);
    }

}
